import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function InstagramSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [lbIndex, setLbIndex] = useState(-1);
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);
    const cols = config.columns || 4;
    const gridCls = cols === 3 ? 'grid-cols-3' : cols === 6 ? 'grid-cols-3 md:grid-cols-6' : 'grid-cols-2 md:grid-cols-4';
    const tp = s.dark;

    const _allImages = content.folder_id && mediaFolders ? (mediaFolders[content.folder_id] || []) : [];
    const _sel = content.selected_file_ids;
    const folderImages = _sel?.length > 0 ? _allImages.filter(f => _sel.includes(f.id)) : _allImages;

    const variant = config.variant || 'grid';
    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    /* ── shared CTA + lightbox bottom ── */
    const renderCta = () => content.cta_text && content.profile_url ? (
        <div className="text-center mt-6">
            <a href={content.profile_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: s.accent }}>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                {content.cta_text}
            </a>
        </div>
    ) : null;

    const renderLightbox = () => (
        <Lightbox open={lbIndex >= 0} close={() => setLbIndex(-1)} index={lbIndex}
            slides={lbIndex >= 0 ? folderImages.map(img => ({ src: img.image_url, alt: img.title || '' })) : []} />
    );

    const emptyMsg = <p className="text-center text-sm py-8" style={{ color: s.textSub }}>Agrega fotos desde la libreria de medios</p>;

    /* ── carousel variant ── */
    if (variant === 'carousel') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || '@tunegocio'} subtitle={content.subtitle} config={config} s={s} />
                    {folderImages.length > 0 ? (
                        <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                            <style>{`.ig-carousel::-webkit-scrollbar{display:none}`}</style>
                            {folderImages.map((img, i) => (
                                <div key={i} className={`ig-carousel shrink-0 w-[calc(50%-6px)] md:w-[calc(20%-10px)] snap-start aspect-square rounded-xl overflow-hidden cursor-pointer group transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 8) * 60}ms` : '0ms' }}
                                    onClick={() => setLbIndex(i)}>
                                    <img src={img.image_url} alt={img.title || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                </div>
                            ))}
                        </div>
                    ) : emptyMsg}
                    {renderCta()}
                </div>
                {renderLightbox()}
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── story-style variant ── */
    if (variant === 'story-style') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title || '@tunegocio'} subtitle={content.subtitle} config={config} s={s} />
                    {folderImages.length > 0 ? (
                        <div className="flex overflow-x-auto gap-3 pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                            <style>{`.ig-story::-webkit-scrollbar{display:none}`}</style>
                            {folderImages.map((img, i) => (
                                <div key={i} className={`ig-story shrink-0 w-32 md:w-40 h-56 md:h-72 rounded-2xl overflow-hidden cursor-pointer group transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                                    style={{ transitionDelay: visible ? `${Math.min(i, 8) * 60}ms` : '0ms' }}
                                    onClick={() => setLbIndex(i)}>
                                    <img src={img.image_url} alt={img.title || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                </div>
                            ))}
                        </div>
                    ) : emptyMsg}
                    {renderCta()}
                </div>
                {renderLightbox()}
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ── grid (default) variant ── */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title || '@tunegocio'} subtitle={content.subtitle} config={config} s={s} />

                {folderImages.length > 0 ? (
                    <>
                    <div className={`grid ${gridCls} gap-2`}>
                        {folderImages.slice(0, visibleCount).map((img, i) => (
                            <div key={i} className={`group relative aspect-square overflow-hidden rounded-lg cursor-pointer transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 50}ms` : '0ms' }}
                                onClick={() => setLbIndex(i)}>
                                <img src={img.image_url} alt={img.title || ''} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                </div>
                            </div>
                        ))}
                    </div>
                    {visibleCount < folderImages.length && (
                        <div className="text-center mt-8">
                            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, folderImages.length))}
                                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                                style={{borderColor: s.accent, color: s.accent}}>
                                Ver mas ({folderImages.length - visibleCount} restantes)
                            </button>
                        </div>
                    )}
                    </>
                ) : emptyMsg}

                {renderCta()}
            </div>
            {renderLightbox()}
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
